import React, { useState } from 'react';
import { toggleReplyLike, deleteReply } from '../services/replyService';
import EditReplyForm from './EditReplyForm';
import MentionFormatter from './MentionFormatter';
import './Reply.css';

const Reply = ({ reply, postId, currentUser }) => {
  const [isLiking, setIsLiking] = useState(false);
  const [showLikedBy, setShowLikedBy] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const handleLike = async () => {
    if (isLiking) return;
    
    setIsLiking(true);
    try {
      await toggleReplyLike(postId, reply.id, currentUser);
    } catch (error) {
      console.error('Error toggling reply like:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteReply(postId, reply.id, currentUser.uid);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting reply:', error);
      alert('Failed to delete reply: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const getUsername = (email) => {
    return email.split('@')[0];
  };

  const getLikedByUsers = () => {
    return (reply.likedByUsers || []).map(user => getUsername(user.email));
  };

  const likedByUsers = reply.likedByUsers || [];
  const isLikedByCurrentUser = likedByUsers.some(user => user.id === currentUser.uid);
  const likesCount = reply.likesCount || 0;

  return (
    <div id={`reply-${reply.id}`} className="reply">
      <div className="reply-header">
        <div className="reply-author">
          <div className="reply-author-avatar">
            {reply.authorEmail?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="reply-author-info">
            <div className="reply-author-email">{reply.authorEmail}</div>
            <div className="reply-time">{formatDate(reply.createdAt)}</div>
          </div>
        </div>
        
        {reply.authorId === currentUser.uid && (
          <div className="reply-actions-menu">
            <button 
              className="edit-reply-button"
              onClick={() => setIsEditing(true)}
              disabled={isDeleting || isEditing}
              title="Edit reply"
            >
              ✏️
            </button>
            <button 
              className="delete-reply-button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting || isEditing}
              title="Delete reply"
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      <div className="reply-content">
        {isEditing ? (
          <EditReplyForm 
            reply={reply}
            postId={postId}
            currentUser={currentUser}
            onEditComplete={() => setIsEditing(false)}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <div>
            <p><MentionFormatter text={reply.text} /></p>
            {reply.isEdited && (
              <div className="reply-edited-indicator">
                <span>✏️ Edited {reply.editedAt ? formatDate(reply.editedAt) : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="reply-actions">
        <div className="reply-like-section">
          <button 
            className={`reply-like-button ${isLikedByCurrentUser ? 'liked' : ''}`}
            onClick={handleLike}
            disabled={isLiking}
            onMouseEnter={() => setShowLikedBy(true)}
            onMouseLeave={() => setShowLikedBy(false)}
          >
            <span className="reply-like-icon">
              {isLikedByCurrentUser ? '👍' : '👍'}
            </span>
            <span className="reply-like-count">{likesCount}</span>
          </button>
          
          {showLikedBy && likedByUsers.length > 0 && (
            <div className="reply-likes-tooltip">
              <div className="reply-likes-tooltip-header">
                Liked by {likesCount} {likesCount === 1 ? 'person' : 'people'}
              </div>
              <div className="reply-likes-tooltip-users">
                {getLikedByUsers().map((username, index) => (
                  <div key={index} className="reply-likes-tooltip-user">
                    {username}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {showLikedBy && likedByUsers.length === 0 && (
            <div className="reply-likes-tooltip">
              <div className="reply-likes-tooltip-header">No likes yet</div>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Delete Reply</h3>
            <p>Are you sure you want to delete this reply? This action cannot be undone.</p>
            <div className="delete-confirmation-actions">
              <button 
                className="delete-confirm-button"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button 
                className="delete-cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reply;